// The quantity stepper: a decrease button, the value, and
// an increase button in one labelled group. Figma draws two sizes: `Stepper/Default`
// (18:15, 116x40, three equal cells) and `Stepper/Touch` (5334:17305, 136x48, three
// 44x44 cells, "44px touch target for mobile and tablet. Desktop keeps Stepper/Default").
// `adaptive` is Touch below 1024px and Default from 1024px, for a host whose one layout
// spans tablet and desktop. `compact` is Stepper/Default drawn at 96x44, as the mobile
// search suggestions place it (Search — Typeahead, Mobile 320, 4002:17759), where a row
// has no room for the wider sizes. The value cell fits three digits at every size; a longer
// value (the line maximum has six) widens the value cell to fit rather than clip it, so
// the stepper grows past its drawn width only then. Extracted
// from Story 8.2's `CartLineRow` so the Previous Orders line selection uses the same
// control rather than a third hand-rolled copy.
//
// The host owns the value and what a step means (a cart edit commits at once; a
// selection only changes a draft). The stepper renders and reports. It never disables
// itself while `busy`: disabling the focused button would drop focus to <body>, so the
// group advertises `aria-busy` instead. `decreaseDisabled` / `increaseDisabled` are for a
// bound the host enforces (a minimum of 1, the line maximum).
//
// With `editable`, the value is a typeable field between the two buttons. The host
// filters each keystroke (`sanitiseInput`) and is told the typed text once, on Enter or
// on blur, only when it differs from `value`. Escape puts `value` back and commits
// nothing. While the field holds no draft it follows `value`, so a step or a server
// reconcile shows at once. A visually hidden copy of the value stays the polite live
// region, so a step is still announced.

import { useState, type CSSProperties, type FocusEvent, type KeyboardEvent } from 'react';
import styles from './QuantityStepper.module.css';

/** A typed value handed to the host. */
export interface QuantityStepperCommit {
  /** The field's text after `sanitiseInput` (may be empty). */
  readonly typed: string;
  /** What committed it: Enter (focus stays on the field) or leaving the field. */
  readonly trigger: 'enter' | 'blur';
  /** On blur, the element focus is moving to (null when none or on Enter). */
  readonly focusTarget: Element | null;
}

/** Makes the value a typeable field (see the header note). */
export interface QuantityStepperEditable {
  /** Names the field for assistive tech (for example "Quantity of 58384"). */
  readonly inputLabel: string;
  /** Filters each change to what the host accepts (digits only, capped). */
  readonly sanitiseInput: (raw: string) => string;
  /** Called on Enter or blur when the typed text differs from `value`. */
  readonly onCommit: (commit: QuantityStepperCommit) => void;
  /** Placed on the field as `data-control`, for a host that moves focus to it. */
  readonly control?: string;
}

export interface QuantityStepperProps {
  readonly value: number;
  /** Names the group for assistive tech (for example "Quantity for 58384"). */
  readonly label: string;
  readonly decreaseLabel: string;
  readonly increaseLabel: string;
  readonly onDecrease: () => void;
  readonly onIncrease: () => void;
  readonly decreaseDisabled?: boolean;
  readonly increaseDisabled?: boolean;
  /** A change is in flight; announced as `aria-busy`, never as a disabled button. */
  readonly busy?: boolean;
  /** `default` is Figma `Stepper/Default` (116x40), `touch` is `Stepper/Touch` (136x48,
   *  44px cells), `adaptive` is Touch below 1024px and Default from 1024px, `compact` is
   *  Stepper/Default at 96x44 (the mobile search suggestion row). */
  readonly size?: 'default' | 'touch' | 'adaptive' | 'compact';
  /** Placed on the decrease button as `data-control`, for a host that moves focus to it. */
  readonly decreaseControl?: string;
  /** Placed on the increase button as `data-control`, for a host that moves focus to it. */
  readonly increaseControl?: string;
  /** Positions the stepper in the host's layout. */
  readonly className?: string | undefined;
  /** Renders the value as a typeable field. */
  readonly editable?: QuantityStepperEditable;
}

export function QuantityStepper({
  value,
  label,
  decreaseLabel,
  increaseLabel,
  onDecrease,
  onIncrease,
  decreaseDisabled = false,
  increaseDisabled = false,
  busy = false,
  size = 'default',
  decreaseControl,
  increaseControl,
  className,
  editable,
}: QuantityStepperProps) {
  // The text being typed; null while the field simply shows `value`.
  const [draft, setDraft] = useState<string | null>(null);
  const shown = String(value);
  // The value cell's width follows the text it shows (the draft while typing), so a value
  // longer than three digits widens the cell instead of being clipped.
  const valueChars = (editable !== undefined && draft !== null ? draft : shown).length;
  const sizing = { '--stepper-value-chars': String(Math.max(valueChars, 1)) } as CSSProperties;

  const commit = (trigger: QuantityStepperCommit['trigger'], focusTarget: Element | null) => {
    if (draft === null) return;
    setDraft(null);
    if (draft !== shown) editable?.onCommit({ typed: draft, trigger, focusTarget });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit('enter', null);
    } else if (event.key === 'Escape' && draft !== null) {
      // Escape undoes the typing only; a host dialog must not also close on it.
      event.stopPropagation();
      setDraft(null);
    }
  };
  const onBlur = (event: FocusEvent<HTMLInputElement>) =>
    commit('blur', event.relatedTarget instanceof Element ? event.relatedTarget : null);

  return (
    <div
      className={`${styles.stepper} ${className ?? ''}`}
      role="group"
      aria-label={label}
      aria-busy={busy}
      data-size={size === 'default' ? undefined : size}
      style={sizing}
    >
      <button
        type="button"
        className={styles.stepButton}
        aria-label={decreaseLabel}
        disabled={decreaseDisabled}
        onClick={onDecrease}
        data-control={decreaseControl}
      >
        <span className={styles.glyph} aria-hidden>
          −
        </span>
      </button>
      {editable === undefined ? (
        <span className={styles.quantity} aria-live="polite">
          {value}
        </span>
      ) : (
        <>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className={`${styles.quantity} ${styles.quantityInput}`}
            aria-label={editable.inputLabel}
            data-control={editable.control}
            value={draft ?? shown}
            onChange={(event) => setDraft(editable.sanitiseInput(event.target.value))}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
          />
          <span className={styles.visuallyHidden} aria-live="polite">
            {value}
          </span>
        </>
      )}
      <button
        type="button"
        className={styles.stepButton}
        aria-label={increaseLabel}
        disabled={increaseDisabled}
        onClick={onIncrease}
        data-control={increaseControl}
      >
        <span className={styles.glyph} aria-hidden>
          +
        </span>
      </button>
    </div>
  );
}
